/**
 * Student create form.
 * Builds profile + establishment + display-name uniqueness check, then POSTs in one submit.
 */
import * as React from 'react';
import { User, BookOpen, Clock, GraduationCap, Users, Building2 } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Checkbox } from '@/components/forms/Checkbox';
import { Textarea } from '@/components/forms/Textarea';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { createStudent, getStudents } from '@/services/inperson/students.service';
import type { CreateStudentDto, CreateStudentFormState } from '@/types/inperson/student.types';
import { fetchRoles } from '@/services/roles.service';
import { getSubjects } from '@/services/subjects.service';
import { getLevels } from '@/services/levels.service';
import { getSchoolClasses, getEstablishment } from '@/services/establishments.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { ProgramCascadeFields } from '@/components/common/ProgramCascadeFields';
import { getSpecialties } from '@/services/specialties.service';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import type { UserWithProfile } from '@/types/user.types';
import { studentToUserWithProfile } from '@/utils/created-user-profile-utils';
import { useAuthStore } from '@/stores/authStore';
import { useEligibleEstablishments } from '@/hooks/useUsers';
import { useEnrichEstablishmentLabels } from '@/hooks/useEnrichEstablishmentLabels';
import { useFormValidation } from '@/hooks/useFormValidation';
import { studentValidationSchema } from '@/utils/validation-schemas-utils';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import {
  DISABILITY_TYPE_OPTIONS,
  SPECIAL_NEEDS_TYPE_OPTIONS,
  STUDENT_FORM_QUERY_KEYS,
  STUDENT_GENDER_OPTIONS,
  BOOLEAN_SELECT_OPTIONS,
} from '@/components/students/student-configs';
import { resolveDefaultCreateSubjectIds } from '@/components/subjects/subject-form-utils';
import {
  appendSelectedEstablishmentOption,
  applyCreateStudentField,
  applyDefaultSubjectsIfEmpty,
  buildCreateStudentDto,
  collectProgramAllowedSlots,
  emptyCreateStudentForm,
  filterLevelsForEstablishmentType,
  findStudentRoleId,
  firstEstablishmentId,
  generateStudentDisplayName,
  IN_PERSON_STUDENT_ELIGIBLE_DEPARTMENTS,
  isCreateStudentFormValid,
  isDisplayNameTaken,
  mapEligibleEstablishmentOptions,
  mapIdLabelOptions,
  mapSpecialtyOptionsForLevel,
  mergeEstablishmentLabelCache,
  nextMaxNomLength,
  shouldApplySingleEstablishmentLock,
  shouldCheckDisplayName,
  shouldClearLastNameOnFirstNameEmpty,
  type EligibleEstablishmentOptionSource,
  type EstablishmentLabelEntry,
} from '@/components/students/inperson/student-form-utils';

/** No-op for locked / loading establishment inputs. */
function ignoreDisabledInputChange(): void {
  return;
}

/** Props for {@link CreateStudentView}. */
interface CreateStudentViewProps {
  onClose: () => void;
  /** Created user (UI profile) so the caller can add them to a session or matching immediately. */
  onSuccess?: (created: UserWithProfile) => void;
}

/** Student create form. */
export const CreateStudentView: React.FC<Readonly<CreateStudentViewProps>> = ({
  onClose,
  onSuccess,
}) => {
  const validation = useFormValidation(studentValidationSchema);

  const [displayNameCheckQuery, setDisplayNameCheckQuery] = React.useState('');
  const [maxNomLength, setMaxNomLength] = React.useState(2);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastNameRef = React.useRef('');
  const prevFirstNameRef = React.useRef('');

  const { data: roles = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.roles,
    queryFn: fetchRoles,
  });

  const { data: subjects = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.subjects,
    queryFn: async () => {
      const response = await getSubjects();
      return response.data;
    },
  });

  const defaultSubjectsAppliedRef = React.useRef(false);

  // Pre-select default subjects once; the user can still add or remove them.
  React.useEffect(() => {
    if (defaultSubjectsAppliedRef.current || subjects.length === 0) return;
    const defaultIds = resolveDefaultCreateSubjectIds(subjects);
    if (defaultIds.length === 0) return;
    defaultSubjectsAppliedRef.current = true;
    setStudentData((prev) => applyDefaultSubjectsIfEmpty(prev, defaultIds));
  }, [subjects]);

  const { data: displayNameCheckResult } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.displayNameCheck(displayNameCheckQuery),
    queryFn: () => getStudents({ search: displayNameCheckQuery, pageSize: 5 }),
    enabled: displayNameCheckQuery.length >= 2,
  });

  const displayNameAlreadyExists = React.useMemo(
    () => isDisplayNameTaken(displayNameCheckQuery, displayNameCheckResult?.data),
    [displayNameCheckQuery, displayNameCheckResult],
  );

  const [studentData, setStudentData] =
    React.useState<CreateStudentFormState>(emptyCreateStudentForm);

  const selectedEstablishmentIds = studentData.establishmentIds;
  const selectedEstablishmentId = firstEstablishmentId(selectedEstablishmentIds);
  const selectedLevelId = studentData.levelId;

  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const { data: eligibleEstablishments = [], isLoading: isLoadingEligible } =
    useEligibleEstablishments(IN_PERSON_STUDENT_ELIGIBLE_DEPARTMENTS, companyId, {
      enabled: !!companyId,
    });

  const allEstablishmentOptions = React.useMemo(
    () =>
      mapEligibleEstablishmentOptions(
        eligibleEstablishments as EligibleEstablishmentOptionSource[],
      ),
    [eligibleEstablishments],
  );

  const [establishmentLabelCache, setEstablishmentLabelCache] = React.useState<Map<string, string>>(
    new Map(),
  );
  const primeEstablishmentLabelCache = React.useCallback((entries: EstablishmentLabelEntry[]) => {
    setEstablishmentLabelCache((prev) => mergeEstablishmentLabelCache(prev, entries));
  }, []);
  useEnrichEstablishmentLabels(selectedEstablishmentIds, primeEstablishmentLabelCache);

  const establishmentOptions = React.useMemo(
    () =>
      appendSelectedEstablishmentOption(
        allEstablishmentOptions,
        selectedEstablishmentId,
        establishmentLabelCache,
      ),
    [allEstablishmentOptions, selectedEstablishmentId, establishmentLabelCache],
  );

  const isSingleEstablishmentLocked = !isLoadingEligible && allEstablishmentOptions.length === 1;
  const lockedEstablishmentId = isSingleEstablishmentLocked
    ? allEstablishmentOptions[0]?.value
    : undefined;
  const lockedEstablishmentLabel = isSingleEstablishmentLocked
    ? allEstablishmentOptions[0]?.label
    : undefined;

  /** Lock a single-establishment actor onto their assigned id so the form never submits empty. */
  React.useEffect(() => {
    if (
      !shouldApplySingleEstablishmentLock(
        isSingleEstablishmentLocked,
        lockedEstablishmentId,
        selectedEstablishmentIds,
      ) ||
      !lockedEstablishmentId
    ) {
      return;
    }
    setStudentData((prev) =>
      applyCreateStudentField(prev, 'establishmentIds', [lockedEstablishmentId]),
    );
  }, [isSingleEstablishmentLocked, lockedEstablishmentId, selectedEstablishmentIds]);

  const { data: levels = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.levels,
    queryFn: async () => {
      const response = await getLevels({ pageSize: 10 });
      return response.data;
    },
  });

  const { data: selectedEstablishmentType } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.establishmentType(selectedEstablishmentId ?? ''),
    queryFn: async () => (await getEstablishment(selectedEstablishmentId!)).type ?? null,
    enabled: !!selectedEstablishmentId,
  });

  const allowedLevels = React.useMemo(
    () => filterLevelsForEstablishmentType(levels, selectedEstablishmentType),
    [levels, selectedEstablishmentType],
  );

  /** Changing establishment type drops a level that is no longer offered there. */
  React.useEffect(() => {
    if (!selectedLevelId || selectedEstablishmentType === undefined || levels.length === 0) return;
    if (allowedLevels.some((level) => level.id === selectedLevelId)) return;
    setStudentData((prev) => applyCreateStudentField(prev, 'levelId', ''));
  }, [selectedLevelId, selectedEstablishmentType, allowedLevels, levels.length]);

  const { data: programsResponse } = useProgramsForMode();
  const programsData = programsResponse?.data ?? [];

  const allowedSlots = React.useMemo(
    () =>
      collectProgramAllowedSlots(
        studentData.programIds,
        programsData as Array<{
          id: string;
          availability?: { weekly?: Array<{ start: string; end: string }> };
        }>,
      ),
    [studentData.programIds, programsData],
  );

  const { data: schoolClassesRes } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.schoolClasses(selectedEstablishmentId ?? '', selectedLevelId),
    queryFn: async () => {
      if (!selectedLevelId) return { data: [], total: 0, page: 1, pageSize: 10 };
      return getSchoolClasses({
        establishmentId: selectedEstablishmentId || undefined,
        levelId: selectedLevelId,
        pageSize: 10,
      });
    },
    enabled: !!selectedEstablishmentId && !!selectedLevelId,
  });
  const schoolClasses = schoolClassesRes?.data ?? [];

  // Load all active specialties with their level links.
  const { data: allSpecialties = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.specialtiesActive,
    queryFn: () => getSpecialties(true),
  });

  const specialtyOptions = React.useMemo(
    () => mapSpecialtyOptionsForLevel(allSpecialties, selectedLevelId),
    [selectedLevelId, allSpecialties],
  );

  React.useEffect(() => {
    const generated = generateStudentDisplayName(studentData.firstName, studentData.lastName);
    if (!generated) return;
    setStudentData((prev) => ({ ...prev, displayName: generated }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (shouldCheckDisplayName(studentData.lastName)) {
      debounceRef.current = setTimeout(() => {
        setDisplayNameCheckQuery(generated);
      }, 600);
    } else {
      setDisplayNameCheckQuery('');
    }
  }, [studentData.firstName, studentData.lastName]);

  // Clear last name only when first name is emptied (not on every typo fix).
  React.useEffect(() => {
    const prev = prevFirstNameRef.current;
    prevFirstNameRef.current = studentData.firstName;
    if (!shouldClearLastNameOnFirstNameEmpty(prev, studentData.firstName)) return;
    setMaxNomLength(2);
    setStudentData((s) => ({ ...s, lastName: '' }));
  }, [studentData.firstName]);

  // Keep the ref in sync with the current lastName.
  React.useEffect(() => {
    lastNameRef.current = studentData.lastName;
  }, [studentData.lastName]);

  // Bump maxNomLength only when displayNameAlreadyExists flips true
  // (omit lastName from deps so each keystroke does not increment).
  React.useEffect(() => {
    if (displayNameAlreadyExists) {
      setMaxNomLength((prev) => nextMaxNomLength(prev, lastNameRef.current));
    }
  }, [displayNameAlreadyExists]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateStudentDto) => createStudent(dto),
    onSuccess: (created) => {
      toast.success('Élève créé avec succès !');
      const profile = studentToUserWithProfile(created);
      onSuccess?.(profile);
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Set one field and re-validate (clears dependent values via {@link applyCreateStudentField}). */
  const handleFieldChange = (field: keyof typeof studentData, value: any) => {
    setStudentData((prev) => applyCreateStudentField(prev, field, value));
    const updatedData = applyCreateStudentField(studentData, field, value);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Persist a single establishment id (or clear). */
  function handleEstablishmentChange(val: string): void {
    handleFieldChange('establishmentIds', val ? [val] : []);
  }

  /** Mark the establishment field touched. */
  function handleEstablishmentBlur(): void {
    handleBlur('establishmentIds');
  }

  /** Loading / single-lock / eligible autocomplete for the establishment field. */
  function renderEstablishmentField(): React.ReactNode {
    if (isLoadingEligible && allEstablishmentOptions.length === 0) {
      return (
        <Input
          label="Établissement"
          value=""
          onChange={ignoreDisabledInputChange}
          placeholder="Chargement des établissements..."
          disabled
          required
          loading
        />
      );
    }
    if (isSingleEstablishmentLocked && lockedEstablishmentLabel) {
      return (
        <Input
          label="Établissement"
          value={lockedEstablishmentLabel}
          onChange={ignoreDisabledInputChange}
          disabled
          required
          helperText="Établissement assigné à votre compte"
          state={validation.getFieldState('establishmentIds')}
          error={validation.getFieldError('establishmentIds')}
        />
      );
    }
    return (
      <Autocomplete
        label="Établissement"
        placeholder="Rechercher un établissement..."
        options={establishmentOptions}
        value={selectedEstablishmentId}
        onChange={handleEstablishmentChange}
        onBlur={handleEstablishmentBlur}
        isLoading={isLoadingEligible}
        helperText={ESTABLISHMENT_SEARCH_HINT}
        required
        state={validation.getFieldState('establishmentIds')}
        error={validation.getFieldError('establishmentIds')}
        inputSize="md"
        minSearchLength={0}
      />
    );
  }

  // Stable handler for ProgramCascadeFields to avoid render-order race conditions.
  const handleProgramCascadeChange = React.useCallback((field: string, value: unknown) => {
    if (field === 'tutoringType') return;
    const key = field === 'programs' ? 'programIds' : field;
    setStudentData((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Validate, resolve the student role, then POST. Guards against double-submit before disable re-renders. */
  const handleSubmit = () => {
    // Guard against double-submit (double-click / double-tap) firing two create requests
    // before the button's disabled state re-renders.
    if (createMutation.isPending) {
      return;
    }

    const isValid = validation.validateForm(studentData);

    if (!isValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    const studentRoleId = findStudentRoleId(roles);
    if (!studentRoleId) {
      toast.error(getErrorMessage('studentRoleNotFound'));
      return;
    }

    createMutation.mutate(buildCreateStudentDto(studentData, studentRoleId));
  };

  const subjectOptions = mapIdLabelOptions(subjects);
  const levelOptions = mapIdLabelOptions(allowedLevels);

  const genderOptions = [...STUDENT_GENDER_OPTIONS];

  const isSubmitting = createMutation.isPending;

  const canSubmit = isCreateStudentFormValid(studentData, displayNameAlreadyExists);

  return (
    <SlidePanelForm
      formType="create"
      title="Créer un élève"
      subtitle="Renseignez les informations de l'élève"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isSubmitting}
      isValid={canSubmit}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <User className="w-4 h-4" />
          Informations personnelles
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Prénom"
            value={studentData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            placeholder="Ex: Marie"
            maxLength={100}
            required
            state={validation.getFieldState('firstName')}
            error={validation.getFieldError('firstName')}
          />
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={!studentData.firstName.trim() ? 'cursor-not-allowed' : ''}>
                  <Input
                    label="Nom"
                    value={studentData.lastName}
                    onChange={(e) =>
                      handleFieldChange('lastName', e.target.value.slice(0, maxNomLength))
                    }
                    onBlur={() => handleBlur('lastName')}
                    placeholder="Ex: MA"
                    maxLength={maxNomLength}
                    required
                    disabled={!studentData.firstName.trim()}
                    state={
                      displayNameAlreadyExists ? 'error' : validation.getFieldState('lastName')
                    }
                    error={
                      displayNameAlreadyExists
                        ? getErrorMessage('displayNameAlreadyExists')
                        : validation.getFieldError('lastName')
                    }
                  />
                </span>
              </TooltipTrigger>
              {!studentData.firstName.trim() && (
                <TooltipContent side="top">
                  <p>Saisissez d'abord le prénom</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <p className="col-span-full text-xs text-muted-foreground -mt-2">
            Le nom de famille est limité à 2 caractères (anonymisation des élèves).
          </p>
          {/* Date of birth — hidden */}
          <div className="col-span-full hidden">
            <DateSplitInput
              label="Date de naissance"
              value={studentData.dateOfBirth}
              onChange={(value) => handleFieldChange('dateOfBirth', value)}
              onBlur={() => handleBlur('dateOfBirth')}
              maxDate={`${new Date().getFullYear() - 3}-12-31`}
            />
          </div>
          <div className="col-span-full">
            <Select
              label="Genre"
              value={studentData.gender}
              onChange={(value) => handleFieldChange('gender', value)}
              onBlur={() => handleBlur('gender')}
              options={genderOptions}
              placeholder="Sélectionner"
              required
              state={validation.getFieldState('gender')}
              error={validation.getFieldError('gender')}
            />
          </div>
        </div>
      </div>

      {/* Address section hidden (GDPR). */}

      <div className="form-section">
        <h4 className="form-section-title">
          <Building2 className="w-4 h-4" />
          Établissement
        </h4>
        <div className="grid grid-cols-1 gap-4">{renderEstablishmentField()}</div>
      </div>

      {selectedEstablishmentIds.length > 0 && (
        <div className="form-section">
          <h4 className="form-section-title">
            <GraduationCap className="w-4 h-4" />
            Scolarité
          </h4>
          <div className="grid grid-cols-1 gap-4">
            <Select
              label="Niveaux académiques"
              placeholder={
                allowedLevels.length === 0 ? 'Aucun niveau disponible' : 'Sélectionner un niveau'
              }
              options={levelOptions}
              value={studentData.levelId}
              onChange={(value) => handleFieldChange('levelId', value)}
              onBlur={() => handleBlur('levelId')}
              required
              state={validation.getFieldState('levelId')}
              error={validation.getFieldError('levelId')}
            />

            {!!selectedLevelId && (
              <Select
                label="Classe"
                value={studentData.schoolClassId}
                onChange={(value) => handleFieldChange('schoolClassId', value)}
                onBlur={() => handleBlur('schoolClassId')}
                options={schoolClasses.map((sc) => ({ value: sc.id, label: sc.name }))}
                placeholder={
                  schoolClasses.length === 0
                    ? 'Aucune classe disponible pour ce niveau'
                    : 'Sélectionner une classe (optionnel)'
                }
              />
            )}

            {specialtyOptions.length > 0 && (
              <MultiSelect
                label="Spécialités"
                placeholder="Sélectionnez les spécialités"
                options={specialtyOptions}
                value={studentData.specialtyIds}
                onChange={(values) => handleFieldChange('specialtyIds', values)}
                onBlur={() => handleBlur('specialtyIds')}
                maxVisibleTags={3}
                helperText="Spécialités disponibles pour ce niveau"
              />
            )}

            <div className="space-y-3">
              <div className="flex items-end pb-2">
                <Checkbox
                  id="hasSpecialNeeds"
                  label="A des besoins spécifiques"
                  checked={studentData.hasSpecialNeeds}
                  onCheckedChange={(checked) => {
                    handleFieldChange('hasSpecialNeeds', checked);
                    if (!checked) {
                      handleFieldChange('specialNeedsType', '');
                      handleFieldChange('specialNeedsNote', '');
                    }
                  }}
                />
              </div>
              {studentData.hasSpecialNeeds && (
                <div className="space-y-3 pl-1">
                  <Select
                    label="Type de besoin spécifique"
                    value={studentData.specialNeedsType}
                    onChange={(value) => {
                      handleFieldChange('specialNeedsType', value);
                      if (value !== 'autre') handleFieldChange('specialNeedsNote', '');
                    }}
                    onBlur={() => handleBlur('specialNeedsType')}
                    options={SPECIAL_NEEDS_TYPE_OPTIONS}
                    placeholder="Sélectionner le type"
                    state={validation.getFieldState('specialNeedsType')}
                    error={validation.getFieldError('specialNeedsType')}
                  />
                  {studentData.specialNeedsType === 'autre' && (
                    <Input
                      label="Préciser le besoin"
                      value={studentData.specialNeedsNote}
                      onChange={(e) => handleFieldChange('specialNeedsNote', e.target.value)}
                      onBlur={() => handleBlur('specialNeedsNote')}
                      placeholder="Décrivez le besoin spécifique"
                      maxLength={200}
                      state={validation.getFieldState('specialNeedsNote')}
                      error={validation.getFieldError('specialNeedsNote')}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <RadioGroup
                name="isDisabledCreate"
                label="Élève en situation de handicap ?"
                layout="horizontal"
                options={BOOLEAN_SELECT_OPTIONS}
                value={studentData.isDisabled == null ? undefined : String(studentData.isDisabled)}
                onValueChange={(v) => {
                  if (v === 'true') {
                    handleFieldChange('isDisabled', true);
                    return;
                  }
                  handleFieldChange('isDisabled', false);
                  handleFieldChange('disabilityTypes', []);
                  handleFieldChange('disabilityOtherNote', '');
                }}
              />
              {studentData.isDisabled && (
                <div className="space-y-3 pl-1">
                  <MultiSelect
                    label="Types de handicap"
                    placeholder="Sélectionner les types"
                    options={DISABILITY_TYPE_OPTIONS}
                    value={studentData.disabilityTypes}
                    onChange={(values) => {
                      handleFieldChange('disabilityTypes', values);
                      if (!values.includes('autre')) handleFieldChange('disabilityOtherNote', '');
                    }}
                    maxVisibleTags={4}
                  />
                  {studentData.disabilityTypes.includes('autre') && (
                    <Input
                      label="Préciser le type de handicap"
                      value={studentData.disabilityOtherNote}
                      onChange={(e) => handleFieldChange('disabilityOtherNote', e.target.value)}
                      placeholder="Décrivez le type de handicap"
                      maxLength={200}
                      required
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ProgramCascadeFields
        programs={programsData as any[]}
        value={{
          tutoringType: 'in_person',
          coursePath: studentData.coursePath,
          programs: studentData.programIds,
        }}
        onChange={handleProgramCascadeChange}
        showTutoringType={false}
      />

      <div className="form-section">
        <h4 className="form-section-title">
          <BookOpen className="w-4 h-4" />
          Matières suivies
        </h4>
        <MultiSelect
          label="Matières"
          placeholder="Sélectionnez les matières"
          options={subjectOptions}
          value={studentData.subjects}
          onChange={(values) => handleFieldChange('subjects', values)}
          onBlur={() => handleBlur('subjects')}
          maxVisibleTags={5}
          helperText="Sélectionnez les matières que l'élève suit"
          state={validation.getFieldState('subjects')}
          error={validation.getFieldError('subjects')}
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Clock className="w-4 h-4" />
          Disponibilités
        </h4>
        <AvailabilityGrid
          value={studentData.availability}
          onChange={(value) => handleFieldChange('availability', value)}
          disabled={false}
          allowedSlots={allowedSlots}
        />
        <p className="text-xs text-muted-foreground mt-2">
          Sélectionnez les créneaux de présence hebdomadaires de l&apos;élève
        </p>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Users className="w-4 h-4" />
          Notes
        </h4>
        <Textarea
          label="Notes"
          value={studentData.medicalNotes}
          onChange={(e) => handleFieldChange('medicalNotes', e.target.value)}
          onBlur={() => handleBlur('medicalNotes')}
          placeholder="Informations ou commentaires sur l'élève"
          maxLength={500}
          rows={3}
          state={validation.getFieldState('medicalNotes')}
          error={validation.getFieldError('medicalNotes')}
          helperText="Informations ou commentaires sur l'élève"
        />
      </div>
    </SlidePanelForm>
  );
};
